import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  FormField,
  FormGrid,
  FormSection,
  isValidChileanPhone,
  PhoneField,
  StepActions,
  ValidatedRutSearch,
} from 'components/forms';
import { type FC, useEffect, useState } from 'react';
import type { InsuranceQuoteFormData } from 'services/types/insuranceQuoteForm';
import validateEmail from 'utils/validations/validateEmail';

interface InsuredDataStepProps {
  formData: InsuranceQuoteFormData;
  businessUnitId: number | null;
  onFormDataChange: (updates: Partial<InsuranceQuoteFormData>) => void;
  onNext: () => void;
  setIsLoading: (loading: boolean) => void;
}

const stripPrefix = (phone: string): string => (phone.startsWith('56') ? phone.slice(2) : phone);

const InsuredDataStep: FC<InsuredDataStepProps> = ({
  formData,
  businessUnitId,
  onFormDataChange,
  onNext,
  setIsLoading,
}) => {
  const [rutInput, setRutInput] = useState(formData.rut);
  const [rutValidated, setRutValidated] = useState(!!formData.personInfo);

  const [email, setEmail] = useState(formData.email);
  const [phoneDigits, setPhoneDigits] = useState(stripPrefix(formData.phone));
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [businessUnitIdError, setBusinessUnitIdError] = useState<string | null>(null);

  useEffect(() => {
    setEmail(formData.email);
    setPhoneDigits(stripPrefix(formData.phone));
  }, [formData.email, formData.phone]);

  const hasValidBusinessUnit = businessUnitId != null && businessUnitId > 0;

  const handleSubmit = () => {
    let hasError = false;

    if (!email.trim()) {
      setEmailError('El email es requerido');
      hasError = true;
    } else if (!validateEmail(email)) {
      setEmailError('Ingresa un email válido');
      hasError = true;
    } else {
      setEmailError(null);
    }

    if (!phoneDigits.trim()) {
      setPhoneError('El teléfono es requerido');
      hasError = true;
    } else if (!isValidChileanPhone(phoneDigits)) {
      setPhoneError('Debe comenzar con 9 y tener 9 dígitos');
      hasError = true;
    } else {
      setPhoneError(null);
    }

    if (!hasValidBusinessUnit) {
      setBusinessUnitIdError('Debe seleccionar una unidad de negocio');
      hasError = true;
    } else {
      setBusinessUnitIdError(null);
    }

    if (hasError || !rutValidated) return;

    onFormDataChange({ email, phone: `56${phoneDigits}` });
    onNext();
  };

  return (
    <Box>
      <FormSection title="Identificación del cliente" description="Ingresa el RUT del cliente para identificarlo.">
        <ValidatedRutSearch
          value={rutInput}
          onChange={setRutInput}
          setIsLoading={setIsLoading}
          validatedPerson={formData.personInfo ?? null}
          onValidated={({ formattedRut, personInfo }) => {
            onFormDataChange({ rut: rutInput, formattedRut, personInfo });
            setRutValidated(true);
          }}
        />
      </FormSection>

      <FormSection title="Datos de contacto" description="Ingresa email y teléfono del cliente.">
        {businessUnitIdError && (
          <Typography variant="body2" color="error" sx={{ mb: 2, fontFamily: 'Jost, sans-serif' }}>
            {businessUnitIdError}
          </Typography>
        )}
        <FormGrid columns={2}>
          <FormField
            label="Email"
            type="email"
            placeholder="cliente@ejemplo.com"
            value={email}
            required
            error={emailError}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(null);
              onFormDataChange({ email: e.target.value });
            }}
            onBlur={() => {
              if (!email.trim()) setEmailError('El email es requerido');
              else if (!validateEmail(email)) setEmailError('Ingresa un email válido');
              else setEmailError(null);
            }}
          />
          <PhoneField
            value={phoneDigits}
            onChange={(digits) => {
              setPhoneDigits(digits);
              if (phoneError) setPhoneError(null);
              onFormDataChange({ phone: `56${digits}` });
            }}
            onBlur={() => {
              if (!phoneDigits.trim()) setPhoneError('El teléfono es requerido');
              else if (!isValidChileanPhone(phoneDigits)) setPhoneError('Debe comenzar con 9 y tener 9 dígitos');
              else setPhoneError(null);
            }}
            required
            error={phoneError}
          />
        </FormGrid>
      </FormSection>

      <StepActions onNext={handleSubmit} nextDisabled={!rutValidated || !email.trim() || phoneDigits.length !== 9} />
    </Box>
  );
};

export default InsuredDataStep;
